import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

type FleetProps = {
    numberOfItem: number | string;
    title: string;
    icon: string;
    color: string;
    metricKey?: string;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const metricFieldByKey: Record<string, string> = {
    vehicles: "total",
    drivers: "total",
    reports: "total",
    users: "total",
    notifications: "count",
    pois: "count",
    tags: "total",
    devices: "total",
    alarms: "count",
    tickets: "count",
    tms: "count",
    sites: "count",
    snapshots: "totalsnapshots",
    vehicle_groups: "total",
    driver_groups: "total",
    device_groups: "total",
};

function formatCompactNumber(value: number) {
    if (value >= 10000) {
        return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
    }
    return String(value);
}

async function fetchCounterMetric(metricKey: string, userId: string | null) {
    if (!backendUrl || !userId) return null;
    const totalBody = JSON.stringify({ id_user: userId, search: "", type: 0 });
    const groupBody = JSON.stringify({ searchValue: null, searchColumn: "" });

    const postTotal = async (endpoint: string, body = totalBody) => {
        const response = await fetch(`${backendUrl}${endpoint}`, {
            method: "POST",
            mode: "cors",
            headers: { "Content-Type": "application/json" },
            body,
        });
        if (!response.ok) throw new Error(`${metricKey} counter failed`);
        return response.json();
    };

    const getTotal = async (endpoint: string) => {
        const response = await fetch(`${backendUrl}${endpoint}`, { mode: "cors" });
        if (!response.ok) throw new Error(`${metricKey} counter failed`);
        return response.json();
    };

    if (metricKey === "vehicles") {
        return getTotal(`/api/vehicle/totalpage/${userId}`);
    }

    if (metricKey === "drivers") {
        return getTotal(`/api/dash/driver/${userId}`);
    }

    if (metricKey === "reports") {
        return postTotal("/api/rapport/totalpage", JSON.stringify({ id_user: userId }));
    }

    if (metricKey === "users") {
        return getTotal(`/api/user/totalpage/${userId}`);
    }

    if (metricKey === "notifications") {
        return postTotal("/api/notification/totalpage", JSON.stringify({ search: "", type: 0 }));
    }

    if (metricKey === "pois") return postTotal("/api/poi/totalpage");
    if (metricKey === "tags") return postTotal("/api/ibutton/totalpage");
    if (metricKey === "devices") return getTotal(`/api/device/totalpage/${userId}`);
    if (metricKey === "alarms") return postTotal("/api/alarm/totalpage");
    if (metricKey === "tickets") return postTotal("/api/ticket/totalpage");
    if (metricKey === "tms") return postTotal("/api/tms/totalpage");
    if (metricKey === "sites") return postTotal("/api/site/totalpage");
    if (metricKey === "snapshots") return getTotal(`/api/snapshot/total/${userId}`);
    if (metricKey === "vehicle_groups") return postTotal(`/api/groups-vehicule/total/${userId}`, groupBody);
    if (metricKey === "driver_groups") return postTotal(`/api/groups-driver/total/${userId}`, groupBody);
    if (metricKey === "device_groups") return postTotal(`/api/groups-device/total/${userId}`, groupBody);

    return null;
}

function extractMetricValue(data: any, metricKey: string) {
    const field = metricFieldByKey[metricKey] || "total";
    const source = Array.isArray(data) ? data[0] : data;
    if (typeof data === "number" || typeof data === "string") return Number(data) || 0;
    if (!source) return 0;
    return Number(source?.[field] ?? source?.total ?? source?.count ?? source?.totalsnapshots ?? source?.[0]?.total ?? 0);
}

const FleetCounter: React.FC<FleetProps & { linkTo: string }> = ({ numberOfItem, title, icon, color, linkTo, metricKey }) => {
    const [metricValue, setMetricValue] = useState<number | string>(numberOfItem);

    useEffect(() => {
        let mounted = true;
        const userId = localStorage.getItem("userid");

        if (!metricKey || metricKey === "custom") {
            setMetricValue(numberOfItem);
            return;
        }

        fetchCounterMetric(metricKey, userId)
            .then((data) => {
                if (!mounted) return;
                const rawValue = extractMetricValue(data, metricKey);
                setMetricValue(formatCompactNumber(rawValue));
            })
            .catch(() => {
                if (mounted) setMetricValue(numberOfItem);
            });

        return () => {
            mounted = false;
        };
    }, [metricKey, numberOfItem]);

    return (
        <div className="card card-block card-stretch card-height">
            <div className="card-body">
                <Link to={linkTo} className='metric-widget' style={{ textDecoration: 'none' }}>
                    <div className="d-flex align-items-center mb-4 card-total-sale">
                        <div className={`icon iq-icon-box-2 ${color}`}>
                            <i className={`las la-${icon}`}></i>
                        </div>
                        <div className='counter-color'>
                            <p className="mb-2" style={{fontSize:"14px"}}>{title}</p>
                            <h4>{metricValue}</h4>
                        </div>
                    </div>
                </Link>
                <div className="iq-progress-bar mt-2">
                    <span className="bg-info iq-progress progress-1" data-percent={Number(metricValue) || 0} style={{ transition: `width 2s ease 0s; width: ${Math.min(100, Number(metricValue) || 0)}%` }}>
                    </span>
                </div>
            </div>
        </div>
    );
}

export default FleetCounter;
